/**
 * Supabase Error Handler
 * 
 * Centralized error handling for Supabase operations with Sentry integration.
 * Filters out expected errors and reports only actionable issues.
 */

import { PostgrestError } from '@supabase/supabase-js';
import { captureException, addBreadcrumb } from '../../lib/sentry';

/**
 * Error codes that should NOT be reported to Sentry
 * These are expected errors that don't indicate bugs
 */
const IGNORED_ERROR_CODES = [
  'PGRST116', // Not found (404) - expected when querying non-existent records
  '23505',    // Unique violation - handled by validation
  '23503',    // Foreign key violation - handled by validation
  '42501',    // Insufficient privilege - handled by RBAC
];

/**
 * Check if a Supabase error should be reported to Sentry
 */
function shouldReportError(error: PostgrestError): boolean {
  // Don't report ignored error codes
  if (IGNORED_ERROR_CODES.includes(error.code)) {
    return false;
  }
  
  // Don't report auth errors (handled by auth flow)
  if (error.message.toLowerCase().includes('jwt')) {
    return false;
  }
  
  return true;
}

/**
 * Handle Supabase errors with Sentry reporting
 * 
 * @param error - Supabase error object
 * @param context - Additional context for debugging
 * @returns The original error (for re-throwing)
 */
export function handleSupabaseError(
  error: PostgrestError,
  context?: {
    operation?: string;
    table?: string;
    userId?: string;
    extra?: Record<string, any>;
  }
): PostgrestError {
  // Add breadcrumb for all errors (even ignored ones)
  addBreadcrumb(
    `Supabase Error: ${error.message}`,
    'database',
    'error',
    {
      code: error.code,
      table: context?.table,
      operation: context?.operation,
    }
  );
  
  // Log to console for development
  console.error('[Supabase Error]', {
    message: error.message,
    code: error.code,
    details: error.details,
    hint: error.hint,
    context,
  });
  
  // Report to Sentry if it's an unexpected error
  if (shouldReportError(error)) {
    captureException(new Error(`Supabase Error: ${error.message}`), {
      tags: {
        errorType: 'database_error',
        service: 'supabase',
        errorCode: error.code,
        table: context?.table || 'unknown',
      },
      extra: {
        supabaseError: {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        },
        operation: context?.operation,
        userId: context?.userId,
        ...context?.extra,
      },
      level: 'error',
    });
  }
  
  return error;
}

/**
 * Wrap async Supabase operations with error handling
 * 
 * Usage:
 * ```typescript
 * const data = await withSupabaseErrorHandling(
 *   async () => {
 *     const { data, error } = await supabase.from('users').select('*');
 *     if (error) throw error;
 *     return data;
 *   },
 *   { operation: 'select', table: 'users' }
 * );
 * ```
 */
export async function withSupabaseErrorHandling<T>(
  operation: () => Promise<T>,
  context?: {
    operation?: string;
    table?: string;
    userId?: string;
    extra?: Record<string, any>;
  }
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw handleSupabaseError(error as PostgrestError, context);
    }
    
    // If it's not a Supabase error, report it anyway
    captureException(error as Error, {
      tags: {
        errorType: 'unexpected_error',
        service: 'supabase',
      },
      extra: context,
      level: 'error',
    });
    
    throw error;
  }
}

